// Constants
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
// Controls
import { Spinner, SpinnerSize } from "@fluentui/react";
import { SmartComponent } from "@ms/uno-controls/lib/local/controls/SmartComponent";
import { LazyCreateTaskPage } from "@ms/uno-controls/lib/local/controls/page/LazyCreateTaskPage";
// Models
import { UnoTheme } from "@ms/uno-models/lib/local/theme/UnoTheme";
// Styles
import { ThemeProvider } from "@fluentui/react";
import { appSpinnerStyles } from "@ms/uno-controls/lib/local/utilities/Mixin.styles";
import { getCreateTaskFromMessageShellClassNames, getCreateTaskFromMessageShellStyles } from "./CreateTaskFromMessageShell.styles";
// Telemetry
import { LoggerContextProvider } from "@ms/uno-controls/lib/local/contexts/LoggerContext";
import { setCurrentRouteInfoForLogs } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
// Utilities
import { Strings as LocaleStrings, loadTranslations } from "@ms/uno-resources/lib/local/CultureUtilities";
import { initializeAndRegisterIcons } from "@ms/uno-utilities/lib/local/ReactUtilities";
import { default as React, Suspense } from "react";
import { Strings } from "../../utilities/CultureUtilities";
import { getThemeV8 } from "../../utilities/ThemeUtilities";
/**
 * CreateTaskFromMessageShell is the root component of the React tree
 */ export class CreateTaskFromMessageShellInternal extends SmartComponent {
    async componentDidMount() {
        const { onLoad, onSuccess, hostAdaptor, storeProviders, loggers } = this.props;
        if (hostAdaptor.canSetDocumentTitle()) {
            document.title = LocaleStrings.LandingPage_PlannerTitle;
        }
        loggers.traceLogger.logTrace(0x1e297754 /* tag_4kx3u */ , TraceLevel.Info, "CreateTaskFromMessageShell component mounted. Notifying load.");
        onLoad();
        const appContextStore = storeProviders.appContext();
        const theme = appContextStore.getAppContext().theme;
        const [v8Theme] = await Promise.all([
            getThemeV8(theme, loggers.traceLogger)
        ]);
        this.setState({
            v8Theme: v8Theme
        });
        this.currentTheme = theme;
        // const routeStore = storeProviders.route();
        this.addStoresToSubscribeTo([
            appContextStore
        ]);
        super.componentDidMount();
        loggers.traceLogger.logTrace(0x1e297753 /* tag_4kx3t */ , TraceLevel.Info, "CreateTaskFromMessageShell mount complete. Notifying success.");
        onSuccess();
        this.runComponentDidMountSideEffects();
    }
    async componentDidUpdate(_prevProps, prevState) {
        const { storeProviders } = this.props;
        // Update theme
        const context = storeProviders.appContext().getAppContext();
        const theme = context.theme;
        if (this.currentTheme !== theme) {
            const v8Theme = await getThemeV8(theme, this.props.loggers.traceLogger);
            this.setState({
                v8Theme
            });
            this.currentTheme = theme;
        }
    }
    /**
     * Name of this component. Used for usage logging.
     */ name() {
        return "CreateTaskFromMessageShell";
    }
    /**
     * Render the CreateTaskFromMessage shell
     */ renderComponent() {
        // If the theme is not loaded yet, don't render anything
        if (!this.state.v8Theme) {
            return null;
        }
        const createTaskFromMessageShellClassNames = this.getCreateTaskFromMessageShellClassNames(this.state.v8Theme);
        return(// The ThemeProvider provides theming for Fluent v8 components
        /*#__PURE__*/ React.createElement(ThemeProvider, {
            theme: this.state.v8Theme
        }, /*#__PURE__*/ React.createElement("div", {
            className: createTaskFromMessageShellClassNames.root
        }, this.renderCreateTaskFromMessage())));
    }
    /**
     * Runs side effects after the component has mounted
     */ async runComponentDidMountSideEffects() {
        const { loggers } = this.props;
        setCurrentRouteInfoForLogs(SubRouteType.MessageTasks, [
            ""
        ], loggers.logHandler);
        this.updateCurrentUserInStore();
    }
    /**
     * Update current user in user store
     */ async updateCurrentUserInStore() {
        const appContext = this.props.storeProviders.appContext().getAppContext();
        const userId = appContext.userId;
        if (userId == null) {
            const msg = "This should never happen. UserId retrieved from app context is null";
            this.props.loggers.traceLogger.logTrace(0x1e297751 /* tag_4kx3r */ , TraceLevel.Error, msg);
            throw new Error(msg);
        }
        (await this.props.actionCreatorProviders.user()).fetchUsers([
            userId
        ], true);
    }
    /**
     * Renders the basic plan view
     */ renderCreateTaskFromMessage() {
        const { actionCreatorProviders, dispatcherEventManagement, hostAdaptor, storeProviders, configProvider } = this.props;
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: this.renderLoadingSpinner()
        }, /*#__PURE__*/ React.createElement(LazyCreateTaskPage, {
            ...this.props,
            actionCreatorProviders: actionCreatorProviders,
            storeProviders: storeProviders,
            dispatcherEventManagement: dispatcherEventManagement,
            configProvider: configProvider,
            dispatchPageReady: this.dispatchPageReady,
            canSetDocumentTitle: hostAdaptor.canSetDocumentTitle()
        }));
    }
    /**
     * Renders a loading spinner
     */ renderLoadingSpinner() {
        return /*#__PURE__*/ React.createElement("div", {
            className: "page-spinner"
        }, /*#__PURE__*/ React.createElement(Spinner, {
            size: SpinnerSize.large,
            className: "spinner",
            label: Strings.UnoShell_SpinnerText,
            styles: appSpinnerStyles
        }));
    }
    /**
     * Get the classnames associated with the computed styles for the CreateTaskFromMessage shell
     */ getCreateTaskFromMessageShellClassNames(currentTheme) {
        return getCreateTaskFromMessageShellClassNames(getCreateTaskFromMessageShellStyles(currentTheme));
    }
    constructor(props, context){
        super(props, context), this.currentTheme = UnoTheme.Light, /**
     * Dispatch pageReady action
     */ this.dispatchPageReady = (dataFetchStart, dataFetchEnd, exportName, metaTag, extraData)=>{
            const { actionCreatorProviders } = this.props;
            const perfCollectionActionCreator = actionCreatorProviders.performanceCollection();
            perfCollectionActionCreator.pageReady(dataFetchStart, dataFetchEnd, exportName, metaTag, extraData);
        };
        this.state = {
            v8Theme: null
        };
        // Initialize icons
        initializeAndRegisterIcons();
        // Initialize resource localization
        loadTranslations(this.props.storeProviders.appContext().getAppContext().locale);
    }
}
export const CreateTaskFromMessageShell = (props)=>{
    return /*#__PURE__*/ React.createElement(LoggerContextProvider, {
        value: props.loggers
    }, /*#__PURE__*/ React.createElement(CreateTaskFromMessageShellInternal, props));
};
